import { userRoleMapping } from "constants/userRole";
import { Label } from "layout/typography/Label";
import { handleChangePhoneWithMask } from "pages/CreateOrder/utils/mask";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Controller, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { UserDetailsFields } from "../CreateEditUser";
import { formValidationConstants } from "../helpers/formValidations";
import { isValidUSPhone } from "../helpers/phoneUtils";

export function UserInfo() {
  const { id } = useParams();

  const {
    control,
    trigger,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const mfaChannel = watch("mfaChannel");

  function getFormErrorMessage(name: string) {
    return (
      errors[name] && (
        <small className="p-error">{errors[name]?.message as string}</small>
      )
    );
  }

  async function handleOnBlurField({
    onBlur,
    field,
  }: {
    onBlur: () => void;
    field: UserDetailsFields;
  }) {
    trigger(field);
    onBlur();
  }

  return (
    <div className="formgrid grid">
      <div className="field col-4">
        <Label htmlFor="name">Name</Label>
        <Controller
          name="name"
          control={control}
          rules={{
            required: formValidationConstants.USER.NAME,
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <>
              <InputText
                id="name"
                className="w-full"
                autoComplete="off"
                value={value || ""}
                placeholder="Enter user name here"
                aria-labelledby="name"
                onBlur={() => {
                  handleOnBlurField({ onBlur, field: "name" });
                }}
                onChange={onChange}
              />

              {getFormErrorMessage("name")}
            </>
          )}
        />
      </div>

      {!id && (
        <div className="field col-4">
          <Label htmlFor="email">Email</Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: formValidationConstants.USER.EMAIL,
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <InputText
                  id="email"
                  className="w-full"
                  autoComplete="off"
                  value={value || ""}
                  placeholder="Enter user email here"
                  aria-labelledby="email"
                  onBlur={() => {
                    handleOnBlurField({ onBlur, field: "email" });
                  }}
                  onChange={onChange}
                />

                {getFormErrorMessage("email")}
              </>
            )}
          />
        </div>
      )}

      <div className="field col-4">
        <Label htmlFor="scope">Scope</Label>
        <Controller
          name="scope"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="scope"
              name="scope"
              value={value}
              className="w-full"
              onChange={onChange}
              placeholder="Select"
              options={Object.values(userRoleMapping)}
            />
          )}
        />
      </div>

      <div className="field col-4">
        <Label htmlFor="analyticsTabEnabled">Analytics Tab Enabled</Label>
        <Controller
          name="analyticsTabEnabled"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="analyticsTabEnabled"
              name="analyticsTabEnabled"
              value={value}
              className="w-full"
              onChange={onChange}
              placeholder="Select"
              options={[
                { label: "True", value: true },
                { label: "False", value: false },
              ]}
            />
          )}
        />
      </div>

      <div className="field col-4">
        <Label htmlFor="contractsOnlyMode">Contracts-Only Access</Label>
        <Controller
          name="contractsOnlyMode"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="contractsOnlyMode"
              name="contractsOnlyMode"
              value={value}
              className="w-full"
              onChange={onChange}
              placeholder="Select"
              options={[
                { label: "True", value: true },
                { label: "False", value: false },
              ]}
            />
          )}
        />
      </div>

      {/* MFA Settings Section */}
      <div className="field col-12 mt-4">
        <h4 className="m-0">MFA Settings</h4>
      </div>

      <div className="field col-4">
        <Label htmlFor="mfaChannel">MFA Channel</Label>
        <Controller
          name="mfaChannel"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Dropdown
              id="mfaChannel"
              name="mfaChannel"
              value={value}
              className="w-full"
              onChange={(e) => {
                onChange(e);
                if (e.value === "email") {
                  setValue("mfaPhoneNumber", "");
                } else if (e.value === "sms") {
                  // Trigger validation when switching to SMS
                  trigger("mfaPhoneNumber");
                }
              }}
              options={[
                { label: "Email", value: "email" },
                { label: "SMS", value: "sms" },
              ]}
            />
          )}
        />
      </div>

      <div className="field col-4">
        <Label htmlFor="mfaPhoneNumber">MFA Phone Number</Label>
        <Controller
          name="mfaPhoneNumber"
          control={control}
          rules={{
            validate: (value) => {
              const channel = getValues("mfaChannel");
              if (channel === "sms" && !value) {
                return formValidationConstants.USER.MFA_PHONE;
              }
              if (value && !isValidUSPhone(value)) {
                return formValidationConstants.USER.MFA_PHONE_FORMAT;
              }
              return true;
            },
          }}
          render={({ field: { onChange, onBlur, value } }) => (
            <>
              <InputText
                id="mfaPhoneNumber"
                className="w-full"
                value={value || ""}
                onChange={(e) => handleChangePhoneWithMask(e, onChange)}
                onBlur={() => {
                  handleOnBlurField({ onBlur, field: "mfaPhoneNumber" });
                }}
                placeholder="(555) 123-4567"
                disabled={mfaChannel !== "sms"}
              />
              {getFormErrorMessage("mfaPhoneNumber")}
            </>
          )}
        />
      </div>
    </div>
  );
}
